import { useApi } from "./api";

export const useAdminService = () => {
  const api = useApi();

  return {
    getReportsMontly: async (selectedDate, signal) => {
      try {
        if (!selectedDate || !isValidDateFormat(selectedDate)) {
          throw new Error('Formato de fecha inválido. Use YYYY-MM-DD');
        }

        const [year, month] = selectedDate.split('-');

        // Configuramos la llamada con AbortController usando la instancia autenticada
        const response = await api.get('/reports/monthly', {
          params: { year, month },
          responseType: 'blob',
          headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
          // Pasamos el signal al axios autenticado
          signal: signal
        });

        // Si la descarga fue cancelada, no continuamos con el proceso
        if (signal?.aborted) {
          throw new Error('Descarga cancelada');
        }

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `Reporte-${year}-${month}.xlsx`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        return { success: true };

      } catch (error) {
        // Manejamos específicamente la cancelación
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          throw new Error('Descarga cancelada por el usuario');
        }

        if (error.response?.data instanceof Blob) {
          const texto = await error.response.data.text();
          try {
            const errorJson = JSON.parse(texto);
            throw new Error(errorJson.message);
          } catch (e) {
            throw new Error('Error al procesar la respuesta del servidor');
          }
        }
        
        throw error.message || 'Error al procesar la solicitud';
      }
    },

    getReportsYear: async (selectedDate) => {
      try {
        // Validación de la fecha
        if (!selectedDate || !isValidDateFormat(selectedDate)) {
          throw new Error('Formato de fecha inválido. Use YYYY-MM-DD');
        }

        // Extraer año de la fecha proporcionada
        const [year] = selectedDate.split('-');

        // Configuración de la petición usando la instancia autenticada
        const response = await api.get('/reports/annual', {
          params: { year },
          responseType: 'blob',
          headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          }
        });

        // Crear el objeto Blob con el tipo MIME correcto para Excel
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        // Crear la URL del blob
        const downloadUrl = window.URL.createObjectURL(blob);

        // Crear un elemento de enlace temporal para la descarga
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `Reporte-${year}.xlsx`;

        // Agregar el enlace al documento, hacer clic y luego removerlo
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Liberar la URL del objeto para liberar memoria
        window.URL.revokeObjectURL(downloadUrl);

        return { success: true };

      } catch (error) {
        // Manejo de errores mejorado para diferentes casos
        if (error.response) {
          // Si el error viene con datos binarios, intentamos convertirlo a texto
          if (error.response.data instanceof Blob) {
            const texto = await error.response.data.text();
            try {
              const errorJson = JSON.parse(texto);
              throw new Error(errorJson.message || 'Error al descargar el archivo');
            } catch (e) {
              throw new Error('Error al procesar la respuesta del servidor');
            }
          }
          throw error.response.data?.message || 'Error al descargar el archivo';
        }
        throw error.message || 'Error al procesar la solicitud';
      }
    },

    // Método adicional para reportes por período (mantiene compatibilidad)
    getReportsPeriod: async (selectedDate) => {
      try {
        if (!selectedDate || !isValidDateFormat(selectedDate)) {
          throw new Error('Formato de fecha inválido. Use YYYY-MM-DD');
        }

        const [year, month] = selectedDate.split('-');

        const response = await api.get('/reports/period', {
          params: { year, month },
          responseType: 'blob',
          headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          }
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `Reporte-Periodo-${year}-${month}.xlsx`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        return { success: true };

      } catch (error) {
        if (error.response?.data instanceof Blob) {
          const texto = await error.response.data.text();
          try {
            const errorJson = JSON.parse(texto);
            throw new Error(errorJson.message || 'Error al descargar el archivo');
          } catch (e) {
            throw new Error('Error al procesar la respuesta del servidor', e);
          }
        }
        
        throw error.response?.data?.message || error.message || 'Error al procesar la solicitud';
      }
    }
  };
};

// Función de utilidad para validar el formato de fecha (sin cambios)
function isValidDateFormat(dateString) {
  const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
  
  if (!dateFormatRegex.test(dateString)) {
    return false;
  }

  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
}

// Exportación de compatibilidad para migración gradual
export const adminService = {
  // Métodos deprecated que requerirán token manual
  _deprecated_getReportsMontly: async (selectedDate, signal, token) => {
    // Implementación de fallback si es necesario
  },
  _deprecated_getReportsYear: async (selectedDate, token) => {
    // Implementación de fallback si es necesario
  }
};